import { ReactNode, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { Button } from '@/components/Button'
import { useTopLayer } from '@/hooks/useTopLayer'
import { useFocusScope } from '@/hooks/useFocusScope'
import { useDismissLayer } from '@/hooks/useDismissLayer'

let openModalCount = 0

interface ModalProps {
	open: boolean
	onClose: () => void
	title?: string
	children: ReactNode
	footer?: ReactNode
	spacesClass?: string
	className?: string
	fullScreen?: boolean
	/** Height tracks the content instead of filling the viewport on small screens. */
	fitContent?: boolean
}

function getSizeClasses (fullScreen: boolean, fitContent: boolean): string {
	if (fullScreen) return 'spaces:h-full'

	const height = fitContent ? 'spaces:h-auto' : 'spaces:h-full spaces:md:h-auto'

	return `spaces:max-w-[480px] ${height} spaces:max-h-[calc(100vh-32px)]`
}

export function Modal ({
	open,
	onClose,
	title,
	children,
	footer,
	spacesClass = '',
	className = '',
	fullScreen = false,
	fitContent = false
}: ModalProps) {
	const overlayRef = useRef<HTMLDivElement>(null)
	const contentRef = useRef<HTMLDivElement>(null)

	useTopLayer(overlayRef, open)
	useDismissLayer(open, onClose)
	useFocusScope(contentRef, open, { trap: true })

	useEffect(() => {
		if (!open) return

		openModalCount++
		document.body.style.overflow = 'hidden'

		return () => {
			openModalCount--
			if (openModalCount === 0) {
				document.body.style.overflow = ''
			}
		}
	}, [open])

	if (!open) return null

	return createPortal(
		<div
			ref={overlayRef}
			className='spaces-app spaces-modal-overlay spaces:fixed spaces:inset-0 spaces:z-[2147483000] spaces:flex spaces:items-center spaces:justify-center spaces:p-16'
		>
			{/* Backdrop */}
			<div
				className='spaces-modal-backdrop spaces:absolute spaces:inset-0 spaces:bg-black/50 spaces:backdrop-blur-[1px]'
				onClick={onClose}
			/>

			{/* Content */}
			<div
				ref={contentRef}
				role='dialog'
				aria-modal='true'
				tabIndex={-1}
				className={`spaces-modal spaces:relative spaces:w-full spaces:bg-canvas-base spaces:rounded-24 spaces:border spaces:border-canvas-3 spaces:shadow-sm-below spaces:overflow-hidden spaces:flex spaces:flex-col spaces:outline-none ${getSizeClasses(fullScreen, fitContent)} ${spacesClass} ${className}`}
			>
				{/* Header */}
				{title && (
					<div className='spaces-modal-header spaces:flex spaces:items-center spaces:gap-8 spaces:shrink-0 spaces:pl-24 spaces:pr-12 spaces:py-12 spaces:border-b spaces:border-canvas-3 spaces:bg-canvas-base'>
						<h2 className='spaces:flex-1 spaces:text-16/24 spaces:font-default spaces:text-content-1'>{title}</h2>
						<Button
							iconOnly='X'
							appearance='ghost'
							color='content4'
							size='md'
							aria-label='Close'
							onClick={onClose}
						/>
					</div>
				)}

				{/* Body */}
				<div
					className={`spaces-modal-body spaces:flex-1 spaces:overflow-x-hidden spaces:bg-canvas-base ${
						fullScreen ? 'spaces:overflow-hidden' : 'spaces:overflow-y-auto spaces:p-24'
					}`}
				>
					{children}
				</div>

				{/* Footer */}
				{footer && (
					<div className='spaces-modal-footer spaces:shrink-0 spaces:flex spaces:items-center spaces:justify-between spaces:p-12 spaces:border-t spaces:border-canvas-3 spaces:bg-canvas-base'>
						{footer}
					</div>
				)}
			</div>
		</div>,
		document.body
	)
}
